import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    inject,
    input,
    InputSignal,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { Browser } from '@capacitor/browser';
import { BackgroundColorDirective, ButtonDirective, ColorDirective, TypographyDirective } from '@dartcounter/ui';
import { SmartDeviceFirmware } from '@dc-core/dc-backend/dc-classes';
import { SmartDeviceFirmwareDeploymentResponse } from '@dc-core/dc-backend/dc-responses';
import { VersionHelper } from '@dc-core/dc-helpers/version.helper';
import { AlertService } from '@dc-core/dc-services/alert.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OldIconComponent } from 'src/app/shared/components/icon/icon.component';
import { LoadingTextComponent } from 'src/app/shared/components/loading-text/loading-text.component';
import { SlideDialogLayoutComponent } from 'src/app/shared/dialogs/slide-dialog-layout/slide-dialog-layout.component';
import { EnsureImplementsAllFields } from 'src/app/shared/services/modal.service';
import { ModalService } from 'src/app/shared/services/modal.service';

import {
    DartCounterSmartDevice,
    SmartDeviceDeploymentProgress,
    SmartDeviceDeploymentState,
} from '../../models/smart-devices.models';
import { OverTheAirUpdateService } from '../../omni/services/over-the-air-update.service';
import { VirtStateService } from '../../omni/services/virt-state.service';
import { SmartDeviceService } from '../../services/smart-device.service';

export interface VirtFirmwareUpdateDialogPayload {
    updateMode: FirmwareUpdateMode | InputSignal<FirmwareUpdateMode>;
    firmware?: SmartDeviceFirmware | InputSignal<SmartDeviceFirmware>;
}

export interface SmartDeviceFirmwareUpdateDialogPayload {
    updateMode: InputSignal<FirmwareUpdateMode>;
    firmware?: InputSignal<SmartDeviceFirmware>;
}

export enum FirmwareUpdateMode {
    UPDATE = 'UPDATE',
    REPAIR = 'REPAIR',
    ROLLBACK = 'ROLLBACK',
    FORCE = 'FORCE',
}

@Component({
    selector: 'app-virt-firmware-update-dialog',
    standalone: true,
    templateUrl: 'virt-firmware-update.dialog.html',
    styleUrl: 'virt-firmware-update.dialog.css',
    imports: [
        CommonModule,
        TranslateModule,
        LoadingTextComponent,
        OldIconComponent,
        SlideDialogLayoutComponent,
        ButtonDirective,
        BackgroundColorDirective,
        TypographyDirective,
        ColorDirective,
    ],
})
export class VirtFirmwareUpdateDialogComponent
    implements
        EnsureImplementsAllFields<SmartDeviceFirmwareUpdateDialogPayload>,
        OnInit,
        OnDestroy,
        EnsureImplementsAllFields<VirtFirmwareUpdateDialogPayload>
{
    public updateMode: InputSignal<FirmwareUpdateMode> = input.required();
    public firmware: InputSignal<SmartDeviceFirmware> = input();

    public loadingMessages: WritableSignal<string[]> = signal([
        'Asking Luke Littler where the Double 10 is',
        'Updating your darts skills for more 180s',
        'Improving your aim',
        'Sharpening your treble 20 instincts...',
        'Checking if your darts are magnetized... 🤔',
        'Recalibrating for next-level bullseyes',
        'Teaching your board to fear your throws',
        'Whispering "just hit the 60" to your darts',
        'Practicing your celebration dance routines',
        'Asking the board nicely to go easier on you',
        'Asking Nathan Aspinall how to handle pressure',
        'Updating to Rob Cross voltage mode ⚡',
        'Getting tips from Chris Dobey on smooth finishes',
        "Installing Keane Barry's youthful reflexes",
        "Syncing with Luke Littler's raw talent",
        "Borrowing Stephen Bunting's finishing finesse",
    ]);

    private modalService: ModalService = inject(ModalService);
    private overTheAirUpdateService: OverTheAirUpdateService = inject(OverTheAirUpdateService);
    private virtStateService: VirtStateService = inject(VirtStateService);
    private smartDeviceService: SmartDeviceService = inject(SmartDeviceService);
    private alertService: AlertService = inject(AlertService);
    private translateService: TranslateService = inject(TranslateService);

    public startedUpdate: WritableSignal<boolean> = signal(false);
    public updateInProgress: Signal<boolean> = computed(() => {
        return this.overTheAirUpdateService.isUpdating();
    });

    public versionHelper = VersionHelper;
    public firmwareUpdateMode = FirmwareUpdateMode;

    public selectedVirt: Signal<DartCounterSmartDevice> = computed(() => {
        return this.virtStateService.currentSmartDevice();
    });

    public currentFirmwareUpdate: Signal<SmartDeviceFirmware> = computed(() => {
        return this.overTheAirUpdateService.currentFirmwareUpdate();
    });

    public deploymentProgress: Signal<SmartDeviceDeploymentProgress> = computed(() => {
        return this.overTheAirUpdateService.deploymentProgress();
    });
    public deploymentState: Signal<SmartDeviceDeploymentState> = computed(() => {
        return this.overTheAirUpdateService.deploymentState();
    });

    public PLEASE_LEAVE_THE_DEVICE_POWERED_ON_DURING_UPDATE: WritableSignal<string> = signal('');

    public ngOnInit(): void {
        $localize`:@@PLEASE_LEAVE_THE_DEVICE_POWERED_ON_DURING_UPDATE:Please leave the ${'device'}:device: powered on during the update`;
        this.PLEASE_LEAVE_THE_DEVICE_POWERED_ON_DURING_UPDATE.set(
            this.translateService.instant('PLEASE_LEAVE_THE_DEVICE_POWERED_ON_DURING_UPDATE', {
                device: 'Virt Cam',
            })
        );

        if (
            this.updateMode() === FirmwareUpdateMode.ROLLBACK ||
            (this.updateMode() === FirmwareUpdateMode.FORCE && this.firmware())
        ) {
            this.startUpdate();
        }
    }

    public startUpdate(): void {
        if (this.startedUpdate()) {
            return;
        }

        this.startedUpdate.set(true);

        // NOTE this only needed if the Omni is below 1.1.0
        // Reboot the Virt
        this.smartDeviceService.reboot(this.selectedVirt().ip_address);

        switch (this.updateMode()) {
            case FirmwareUpdateMode.UPDATE:
                this.overTheAirUpdateService
                    .updateDevice(this.selectedVirt().id)
                    .then((result) => {
                        this.handleDeploymentResponse(result.data);
                    })
                    .catch((err) => {
                        this.handleDeploymentError(err);
                    })
                    .finally(() => this.startedUpdate.set(false));
                break;
            case FirmwareUpdateMode.REPAIR:
                this.overTheAirUpdateService
                    .repairDevice(this.selectedVirt().id)
                    .then((result) => {
                        this.handleDeploymentResponse(result.data);
                    })
                    .catch((err) => {
                        this.handleDeploymentError(err);
                    })
                    .finally(() => this.startedUpdate.set(false));
                break;
            case FirmwareUpdateMode.ROLLBACK:
                this.overTheAirUpdateService
                    .rollback(this.selectedVirt().id)
                    .then((result) => {
                        this.handleDeploymentResponse(result.data);
                    })
                    .catch((err) => {
                        this.handleDeploymentError(err);
                    })
                    .finally(() => this.startedUpdate.set(false));
                break;
            case FirmwareUpdateMode.FORCE:
                this.overTheAirUpdateService
                    .override(this.selectedVirt().id, this.firmware().id)
                    .then((result) => {
                        this.handleDeploymentResponse(result.data);
                    })
                    .catch((err) => {
                        this.handleDeploymentError(err);
                    })
                    .finally(() => this.startedUpdate.set(false));
                break;
        }
    }

    public retry(): void {
        this.overTheAirUpdateService.resetState();
        this.startUpdate();
    }

    public reboot(): void {
        this.smartDeviceService.reboot(this.selectedVirt().ip_address);
        this.dismiss();
    }

    private handleDeploymentError(err) {
        this.alertService.errorFromApi(err);
        this.dismiss();
    }

    public openReleaseNotes(): void {
        if (this.currentFirmwareUpdate()?.release_notes_key) {
            const releaseNotesUrl = `https://dartcounter.net/release-notes/omni/${this.currentFirmwareUpdate().release_notes_key}`;
            Browser.open({ url: releaseNotesUrl });
        }
    }

    public dismiss(): void {
        this.modalService.dismiss();
    }

    private handleDeploymentResponse(response: SmartDeviceFirmwareDeploymentResponse) {
        if (response.deployment_id) {
            this.overTheAirUpdateService.updatingDevice.set(true);
            this.overTheAirUpdateService.watchDeploymentStatus(this.selectedVirt().id, response.deployment_id);
        } else {
            console.warn('No deployment ID found', response);
        }
    }

    public ngOnDestroy(): void {
        this.overTheAirUpdateService.resetState();
    }
}
